import { zodResolver } from '@hookform/resolvers/zod';
import {
  ASSIGNABLE_ROLES,
  type CreateInvitationResponse,
  createMemberRequestSchema,
  emailSchema,
} from '@familywallet/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { InviteLink } from '../components/InviteLink';
import { PageHeader } from '../components/PageHeader';
import { Button, buttonVariants } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { invitationsKey, membersKey, useIdempotencyKey } from '../lib/members';

const formSchema = createMemberRequestSchema
  .extend({ invite: z.boolean(), email: z.union([emailSchema, z.literal('')]) })
  .refine((v) => !v.invite || v.email !== '', { path: ['email'], message: 'required' });
type FormValues = z.input<typeof formSchema>;

export function AddMemberPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const canInvite = useCan('members.invite');
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const [created, setCreated] = useState<CreateInvitationResponse | null>(null);
  const form = useForm<FormValues, unknown, z.output<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { displayName: '', role: 'child', invite: false, email: '' },
  });
  const invite = useWatch({ control: form.control, name: 'invite' });
  const role = useWatch({ control: form.control, name: 'role' });
  const roles = ASSIGNABLE_ROLES.filter((r) => r !== 'parent' || me.data?.member.role === 'owner');

  const save = useMutation({
    mutationFn: async (v: z.output<typeof formSchema>) => {
      if (v.invite) {
        return {
          kind: 'invited' as const,
          invitation: await api.invitations.create(
            { email: v.email, displayName: v.displayName, role: v.role },
            { idempotencyKey },
          ),
        };
      }
      return {
        kind: 'added' as const,
        member: await api.members.create(
          { displayName: v.displayName, role: v.role },
          { idempotencyKey },
        ),
      };
    },
    onSuccess: async (result) => {
      renewKey();
      await queryClient.invalidateQueries({ queryKey: membersKey });
      if (result.kind === 'invited') {
        await queryClient.invalidateQueries({ queryKey: invitationsKey });
        setCreated(result.invitation);
      } else {
        await navigate(`/members/${result.member.id}`);
      }
    },
  });

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader
          title={t('invite.title', { name: created.memberDisplayName })}
          back="/members"
        />
        <InviteLink url={created.inviteUrl} name={created.memberDisplayName} />
        <Link to="/members" className={buttonVariants({ variant: 'secondary' })}>
          {t('invite.done')}
        </Link>
      </div>
    );
  }

  const errors = form.formState.errors;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('addMember.title')} back="/members" />
      <p className="text-muted">{t('addMember.intro')}</p>
      <Card>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
        >
          <FormError error={save.error} />
          <Field label={t('fields.memberName')} error={errors.displayName}>
            <Input {...form.register('displayName')} />
          </Field>
          <Field label={t('fields.role')} hint={t(`roleHints.${role}`)} error={errors.role}>
            <Select {...form.register('role')}>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role}`)}
                </option>
              ))}
            </Select>
          </Field>
          {canInvite && (
            <label className="flex min-h-11 items-center gap-3">
              <input
                type="checkbox"
                className="size-5 accent-accent"
                {...form.register('invite')}
              />
              <span>{t('addMember.inviteToggle')}</span>
            </label>
          )}
          {invite && (
            <Field label={t('addMember.inviteEmail')} error={errors.email} kind="email">
              <Input
                type="email"
                inputMode="email"
                autoComplete="off"
                {...form.register('email')}
              />
            </Field>
          )}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending
              ? t('common.saving')
              : invite
                ? t('addMember.submitInvite')
                : t('addMember.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
